// Central definition of which dashboard requires which role, and how to verify it.
//
// Verification strategy per role:
//  - organizer / bar / customer: server-verified via /api/auth/whoami (session cookie).
//  - master / admin:              server-verified via /api/master/check (mn_master cookie).
//  - driver / club / fleet:       client-verified via a per-tab login marker, because
//                                 these flows currently have no real backend session.
//                                 The marker is only set after going through their login
//                                 page, so a direct dashboard URL is still blocked.

export type DashboardRole =
  | 'organizer' | 'bar' | 'customer' | 'master' | 'admin'
  | 'driver' | 'club' | 'fleet';

interface DashboardDef {
  role: DashboardRole;
  loginPath: string;
  verify: 'whoami' | 'master' | 'client';
}

// Order matters: longer/more specific prefixes are matched first.
const DASHBOARD_DEFS: Array<{ prefix: string; def: DashboardDef }> = [
  { prefix: '/dashboard/club',      def: { role: 'club',      loginPath: '/club-login',      verify: 'client' } },
  { prefix: '/dashboard/organizer', def: { role: 'organizer', loginPath: '/organizer-login', verify: 'whoami' } },
  { prefix: '/dashboard/bar',       def: { role: 'bar',       loginPath: '/bar-login',       verify: 'whoami' } },
  { prefix: '/dashboard/driver',    def: { role: 'driver',    loginPath: '/driver-login',    verify: 'client' } },
  { prefix: '/dashboard/fleet',     def: { role: 'fleet',     loginPath: '/fleet-login',     verify: 'client' } },
  { prefix: '/dashboard/master',    def: { role: 'master',    loginPath: '/master-login',    verify: 'master' } },
  { prefix: '/dashboard/customer',  def: { role: 'customer',  loginPath: '/login',           verify: 'whoami' } },
  { prefix: '/dashboard/admin',     def: { role: 'admin',     loginPath: '/fleet-login',     verify: 'master' } },
];

export const getDashboardDef = (path: string): DashboardDef | null => {
  const clean = (path || '').split('?')[0];
  for (const { prefix, def } of DASHBOARD_DEFS) {
    if (clean.startsWith(prefix)) return def;
  }
  return null;
};

const CLIENT_MARKER_KEY = 'activeDashboardRole';

/**
 * Mark a simulated (client-only) dashboard login as successful. Called by the
 * driver/club/fleet login flows so a subsequent dashboard render is allowed.
 */
export const markClientDashboardLogin = (role: DashboardRole) => {
  try { sessionStorage.setItem(CLIENT_MARKER_KEY, role); } catch (e) { /* ignore */ }
};

export const clearClientDashboardLogin = () => {
  try { sessionStorage.removeItem(CLIENT_MARKER_KEY); } catch (e) { /* ignore */ }
};

const hasClientMarker = (role: DashboardRole): boolean => {
  try { return sessionStorage.getItem(CLIENT_MARKER_KEY) === role; } catch (e) { return false; }
};

export type GuardResult = 'authorized' | 'denied';

/**
 * Verify the current visitor is allowed to view the given dashboard role.
 * Resolves to 'authorized' or 'denied'. Network/parse errors fail closed ('denied').
 */
export async function verifyDashboardAccess(def: DashboardDef): Promise<GuardResult> {
  try {
    if (def.verify === 'client') {
      return hasClientMarker(def.role) ? 'authorized' : 'denied';
    }

    if (def.verify === 'master') {
      const res = await fetch('/api/master/check', { method: 'GET', credentials: 'include' });
      const j = await res.json().catch(() => null);
      return j && j.authenticated ? 'authorized' : 'denied';
    }

    // whoami-based roles (organizer / bar / customer)
    const res = await fetch('/api/auth/whoami', { method: 'GET', credentials: 'include' });
    const j = await res.json().catch(() => null);
    if (!j || !j.loggedIn) return 'denied';
    const serverRole = String(j.role || (j.user && j.user.role) || '').toLowerCase();

    if (def.role === 'customer') {
      // Any logged-in non-partner user may view the customer dashboard.
      return (serverRole === 'user' || serverRole === '' || serverRole === 'customer') ? 'authorized' : 'denied';
    }
    return serverRole === def.role ? 'authorized' : 'denied';
  } catch (e) {
    return 'denied';
  }
}
